// What a page puts on the Dock (shell/phone.tsx `Dock`), and the Dock's way
// up.
//
// Promise:
// - `cluster` (one button, the left slot) and `tools` (beside the folded Dock):
//   the page's round buttons are drawn into the Dock's own row (a portal: they
//   stay in the page's tree, its context and state). Off a phone they are not
//   drawn (the page shows its own form of them). Where a host draws the Dock
//   (a native app) they are not drawn either: each button hands its data to
//   the host instead (`HostedDock`, `DockButtonProps.tool`).
// - `input`: the page's post box stands right above the Dock, in the page's
//   flow, as wide as the Dock's row (the left slot and the Dock): the same
//   place on every screen, and the Dock under it stays whole (the person,
//   2026-10-28). While the person writes in it the Dock sinks away and the
//   box comes down onto the keyboard. Off a phone the box stays as it is. The
//   elements around it are the same either way, so crossing a phone's width
//   never builds it again (its focus and draft stay).
// - In an overlay (overlay.tsx), which has no Dock, the box takes the bottom
//   row with the close button at its right, as below.
// - Once written in, the row stays wide while the box holds a draft (text or
//   files), also after the keyboard closed: bringing the buttons back pressed
//   a half-written message into a narrow box (2026-09-27). The box then
//   offers to shrink (`DockInputRow.held` / `release`); when the draft is
//   gone (sent, cleared) the row comes back by itself. A draft kept from
//   before does not hold the row until the box is written in.
// - The buttons' room goes at once (sizes that move while the keyboard rises
//   put the box where iOS did not expect it, an empty frame floating over the
//   keyboard, 2026-09-26), and come back in 200 ms (their room widens, they
//   grow in), when the keyboard is down. Widening, the box's look opens out
//   from the narrow box it was, to the sides and up at once (a clip, 200 ms;
//   its size is final at once) while the buttons, out of reach already,
//   shrink and fade where they stood in the same 200 ms: the box pushes them
//   away (the old web's; dropping them at once lost that push, 2026-10-18).
// - While the Dock grown from the round button lies over the row, the box is
//   squeezed from the right by it, fades and cannot be touched (menu.tsx).
// - While the Dock is stowed (a drawer over the screen's bottom), the whole
//   row sinks away and cannot be touched; its room stays.
import {
  type ReactNode,
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import {
  DockInputContext,
  type DockInputRow,
  type DockProps,
  type DockUpProps,
} from "../shell/phone";
import { HostedDock, usePhoneState } from "./context";
import { DockUpButton, RoundMenuButton } from "./dock-row";
import { DOCK_ITEM_WIDTH } from "./items";
import { useTypingIn } from "./typing";

/** The gap between the box and a button beside it. */
const SIDE_GAP = 7;

export function DockSlot({ kind, present = true, children }: DockProps) {
  const state = usePhoneState();
  const on = !!state?.active && state.menu !== "drawer";
  const claim = state?.claim;
  // frontend-development: not a fetch; tells the frame what holds the Dock.
  useLayoutEffect(() => {
    if (on && claim) return claim(kind);
  }, [on, claim, kind]);
  if (kind !== "input") {
    if (state?.hostDraws)
      return on && present ? (
        <HostedDock.Provider value={true}>{children}</HostedDock.Provider>
      ) : null;
    const host = kind === "cluster" ? state?.clusterHost : state?.toolsHost;
    if (!on || !host || !present) return null;
    return createPortal(children, host);
  }
  return <InputRow on={on}>{children}</InputRow>;
}

function InputRow({ on, children }: { on: boolean; children: ReactNode }) {
  const state = usePhoneState();
  const slot = useRef<HTMLDivElement>(null);
  const typing = useTypingIn((el) => slot.current?.contains(el) === true, on);
  // What the box said (useDockInput) and whether it was written in since the
  // row was last narrow.
  const [draft, setDraft] = useState(false);
  const [touched, setTouched] = useState(false);
  if (typing && !touched) setTouched(true);
  if (touched && !typing && !draft) setTouched(false);
  const held = on && touched && draft && !typing;
  const wide = on && (typing || held);
  // The buttons grow back in only after they have left once (not on opening).
  const [left, setLeft] = useState(false);
  if (wide && !left) setLeft(true);
  const release = useCallback(() => setTouched(false), []);
  const expanded = on && state?.menuState?.from === "button";
  // Over the Dock (the frame's): alone in its row. In an overlay: beside its
  // close button.
  const overDock = on && state?.end === undefined;
  const up = on && !overDock ? state?.up : null;
  const others = (state?.all ?? []).filter((e) => e.key !== state?.here?.key);
  const crowded = on && !wide;
  const row = useMemo<DockInputRow | null>(
    () =>
      on
        ? overDock
          ? { writing: typing, held: false, crowded: false, release, setDraft }
          : { writing: typing, held, crowded, release, setDraft }
        : null,
    [on, overDock, typing, held, crowded, release],
  );
  // Widening, the box opens out from the narrow box it was, both ways in one
  // move: its size is final at once (above), only its look starts cut to
  // the narrow box (in at the sides where the buttons were, down at the top
  // by what it grew: one line to two and the pills). Opening the sides first
  // and growing up after read as two steps (the person, 2026-10-18).
  const fromLeft = up ? DOCK_ITEM_WIDTH + SIDE_GAP : -8;
  /** The lane's height while narrow, the shape the widening starts from. */
  const narrowHeight = useRef<number | null>(null);
  // frontend-development: not a fetch; keeps the narrow box's height for the widening.
  useLayoutEffect(() => {
    if (!wide && on) narrowHeight.current = slot.current?.offsetHeight ?? null;
  });
  // frontend-development: not a fetch; the widening's look.
  // biome-ignore lint/correctness/useExhaustiveDependencies: only when it widens
  useLayoutEffect(() => {
    const el = slot.current;
    if (
      !wide ||
      overDock ||
      !el?.animate ||
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches
    )
      return;
    const room = DOCK_ITEM_WIDTH + SIDE_GAP;
    const before = narrowHeight.current;
    const grew = before ? el.offsetHeight - before : 0;
    const fromTop = grew > 0 ? grew : -8;
    const opening = el.animate(
      [
        {
          clipPath: `inset(${fromTop}px ${room}px -8px ${fromLeft}px round 24px)`,
        },
        { clipPath: "inset(-8px -8px -8px -8px round 24px)" },
      ],
      { duration: 200, easing: "ease-out" },
    );
    return () => opening.cancel();
  }, [wide]);
  // Stowed: the whole row sinks and fades, then is hidden; its room stays (a
  // drawer lies over it, and the chat above must not move).
  const stowed = on && state?.stowed === true;
  return (
    <div
      data-phone-input={on ? "" : undefined}
      data-over-dock={overDock ? "" : undefined}
      data-stowed={stowed ? "" : undefined}
      data-wide={wide ? "" : undefined}
      inert={stowed || undefined}
      className={
        on
          ? `flex items-end transition-[opacity,translate,visibility] duration-200 ease-out motion-reduce:transition-none ${stowed ? "invisible translate-y-3 opacity-0" : ""}`
          : "contents"
      }
    >
      {up ? (
        <BesideBox at="start" away={wide} back={left}>
          <DockUpButton to={up.to} label={up.label} />
        </BesideBox>
      ) : null}
      {/* Squeezed from the right by the Dock grown from the round button
          (menu.tsx), clipped, never resized; no CSS transition on its
          opacity: it would win over that move. */}
      <div
        ref={slot}
        data-phone-lane={on ? "" : undefined}
        data-phone-squeeze={on ? "clip" : undefined}
        inert={expanded || undefined}
        className={
          on ? `min-w-0 flex-1 ${expanded ? "opacity-0" : ""}` : "contents"
        }
      >
        <DockInputContext.Provider value={row}>
          {children}
        </DockInputContext.Provider>
      </div>
      {on && state && !overDock ? (
        <BesideBox at="end" away={wide} back={left}>
          {state.end ?? (
            <RoundMenuButton
              ref={state.holdMenuButton}
              here={state.here}
              others={others}
              open={state.menuState !== null}
              // The whole menu (the panel too) at one tap: opening the Dock
              // first and then More took two (the person, 2026-10-18: "open
              // it this far at one tap"; the old web took two as well).
              onOpen={(p, button) => state.openMenu("button", button, true, p)}
            />
          )}
        </BesideBox>
      ) : null}
    </div>
  );
}

/**
 * A button's room beside the box. Away: no room at once, and the button,
 * out of reach, shrinks and fades where it stood (200 ms) while the box
 * opens out over it: the box pushes it away. Back: the room widens and the
 * button grows in (200 ms), once it has left.
 */
function BesideBox({
  at,
  away,
  back,
  children,
}: {
  at: "start" | "end";
  away: boolean;
  /** It has left before: coming back moves (opening the screen does not). */
  back: boolean;
  children: ReactNode;
}) {
  const motion = back
    ? "transition-[width] duration-200 ease-out motion-reduce:transition-none"
    : "";
  return (
    <div
      data-beside-box={at}
      data-away={away ? "" : undefined}
      // Leaving, it is only a look: nothing to press or read.
      inert={away || undefined}
      aria-hidden={away || undefined}
      className={`relative h-12 flex-none ${away ? "" : motion}`}
      style={{ width: away ? 0 : DOCK_ITEM_WIDTH + SIDE_GAP }}
    >
      {/* Pinned to the row's edge, so it stays where it stood while its
          room is gone (drawn over the box's edge). */}
      <div
        className={`absolute bottom-0 ${at === "start" ? "left-0" : "right-0"} transition-[opacity,scale] duration-200 ease-out motion-reduce:transition-none ${
          away ? "pointer-events-none scale-75 opacity-0" : ""
        }`}
      >
        {children}
      </div>
    </div>
  );
}

/** Names the Dock's way up while mounted (see shell/phone.tsx `DockUp`). */
export function DockUp({ to, label, strong }: DockUpProps) {
  const pushUp = usePhoneState()?.pushUp;
  // frontend-development: not a fetch; tells the frame where up goes.
  useLayoutEffect(
    () => pushUp?.({ to, label, strong }),
    [pushUp, to, label, strong],
  );
  return null;
}
